import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FileText, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RequestQuote = ({ cart }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fromCart } = location.state || { fromCart: false };
  
  const [loading, setLoading] = useState(false);
  const [quoteCreated, setQuoteCreated] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (fromCart && cart.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    setLoading(true);

    try {
      const quoteData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone || undefined,
        customer_address: formData.address || undefined,
        notes: formData.notes || undefined
      };

      if (fromCart) {
        quoteData.items = cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        }));
      } else {
        // For custom quotes, add required fields
        quoteData.service_type = "autre"; // Default service type
        quoteData.message = formData.notes || "Demande de devis personnalisé";
      }

      const endpoint = fromCart ? `${API}/quotes/from-cart` : `${API}/quotes/custom`;
      const response = await axios.post(endpoint, quoteData);
      
      setQuoteCreated(response.data);
      toast.success('Devis créé avec succès !');
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Erreur lors de la création du devis');
    } finally {
      setLoading(false);
    }
  };

  const downloadQuote = async (format) => {
    try {
      const response = await axios.get(
        `${API}/quotes/${quoteCreated.id}/download/${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `devis_${quoteCreated.quote_number}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Devis téléchargé en ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error downloading quote:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (quoteCreated) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-xl p-8" data-testid="quote-created-success">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-12 h-12 text-primary" />
              </div>
              <h1 className="font-heading font-semibold text-3xl mb-4">Devis Créé avec Succès !</h1>
              <p className="text-muted-foreground mb-2">
                Numéro de devis : <span className="font-bold text-primary" data-testid="quote-number">{quoteCreated.quote_number}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Valide jusqu'au : {new Date(quoteCreated.valid_until).toLocaleDateString('fr-FR')}
              </p>
            </div>

            <div className="bg-muted rounded-lg p-6 mb-8">
              <h3 className="font-semibold mb-4">Résumé du devis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sous-total HT :</span>
                  <span className="font-medium">{quoteCreated.subtotal.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">TVA (20%) :</span>
                  <span className="font-medium">{quoteCreated.tax_amount.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="font-semibold">Total TTC :</span>
                  <span className="text-2xl font-bold text-primary" data-testid="quote-total">{quoteCreated.total.toFixed(2)} DH</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground mb-4">
                Téléchargez votre devis dans le format de votre choix :
              </p>
              
              <button
                onClick={() => downloadQuote('pdf')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                data-testid="download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                Télécharger en PDF
              </button>
              
              <button
                onClick={() => downloadQuote('word')}
                className="w-full bg-white border border-input hover:bg-muted h-12 px-8 rounded-full font-medium transition-all flex items-center justify-center gap-2"
                data-testid="download-word-btn"
              >
                <Download className="w-5 h-5" />
                Télécharger en Word
              </button>
              
              <button
                onClick={() => downloadQuote('excel')}
                className="w-full bg-white border border-input hover:bg-muted h-12 px-8 rounded-full font-medium transition-all flex items-center justify-center gap-2"
                data-testid="download-excel-btn"
              >
                <Download className="w-5 h-5" />
                Télécharger en Excel
              </button>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => navigate('/devis')}
                className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 h-12 rounded-full font-medium transition-all"
                data-testid="view-all-quotes-btn"
              >
                Voir tous mes devis
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 bg-white border border-input hover:bg-muted h-12 rounded-full font-medium transition-all"
                data-testid="back-home-btn"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4" data-testid="request-quote-title">
          {fromCart ? 'Demander un Devis' : 'Demande de Devis Personnalisé'}
        </h1>
        <p className="text-base leading-relaxed text-muted-foreground mb-8">
          {fromCart 
            ? 'Remplissez vos informations pour recevoir un devis détaillé pour les produits de votre panier.'
            : 'Décrivez votre projet et recevez un devis personnalisé adapté à vos besoins.'}
        </p>

        {fromCart && cart.length > 0 && (
          <div className="bg-white rounded-xl p-6 mb-8" data-testid="cart-summary">
            <h3 className="font-semibold mb-4">Produits sélectionnés ({cart.length})</h3>
            <div className="space-y-3">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-sm" data-testid={`quote-item-${item.id}`}>
                  <span>{item.name} x{item.quantity}</span>
                  <span className="font-medium">{(item.price * item.quantity).toFixed(2)} DH</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 space-y-6" data-testid="quote-request-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="name">
                Nom complet *
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                placeholder="Jean Dupont"
                required
                data-testid="quote-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="email">
                Email *
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                placeholder="jean.dupont@example.com"
                required
                data-testid="quote-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="phone">
                Téléphone
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                placeholder="06 61 83 71 37"
                data-testid="quote-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="address">
                Adresse
              </label>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full h-12 bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4"
                placeholder="Meknes"
                data-testid="quote-address-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" htmlFor="notes">
              Notes ou demandes particulières
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full bg-white border-input focus:ring-2 focus:ring-primary/20 focus:border-primary rounded-lg transition-all px-4 py-3 resize-none"
              placeholder="Précisions sur votre projet, délais souhaités, questions..."
              data-testid="quote-notes-textarea"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              data-testid="submit-quote-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Créer le devis
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-white border border-input hover:bg-muted h-12 px-8 rounded-full font-medium transition-all"
              data-testid="cancel-quote-btn"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};